/**
 * Instructors Store - Trainer-Stammdaten
 *
 * IndexedDB-backed cache — Trainer-Liste bleibt auch nach
 * Seiten-Reload offline verfügbar.
 */

import { defineStore } from 'pinia'
import { ref } from 'vue'
import { instructorsApi } from '@/api/instructors.api'
import { db } from '@/db'
import type { InstructorSummaryDto } from '@/types/api.types'

export const useInstructorsStore = defineStore('instructors', () => {
  // ==========================================================================
  // State
  // ==========================================================================

  const instructors = ref<InstructorSummaryDto[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // ==========================================================================
  // Actions
  // ==========================================================================

  /**
   * Alle Trainer laden — IndexedDB-first, dann API (wenn online).
   *
   * Offline: gibt gecachte Liste zurück (kein Fehler wenn offline und Cache vorhanden).
   * Online:  API-Daten ersetzen den Cache nach jedem erfolgreichen Fetch.
   */
  async function fetchAll(): Promise<void> {
    // Bereits geladen → kein weiterer Aufruf (In-Memory-Cache)
    if (instructors.value.length > 0) return

    isLoading.value = true
    error.value = null

    try {
      // 1. IndexedDB zuerst (sofortiger, flicker-freier Initialzustand)
      const cached = await db.instructors.toArray()
      if (cached.length > 0) {
        instructors.value = cached
      }

      // 2. API nur wenn online
      if (navigator.onLine) {
        const data = await instructorsApi.getAll()
        const mapped = data
          .map((i) => ({ id: i.id, name: i.fullName.trim() }))
          .sort((a, b) => a.name.localeCompare(b.name, 'de'))
        instructors.value = mapped

        // IndexedDB-Cache aktualisieren
        await db.instructors.clear()
        await db.instructors.bulkPut(mapped)
      } else if (cached.length === 0) {
        error.value = 'Trainer-Liste nicht verfügbar (offline)'
      }
    } catch (e) {
      if (instructors.value.length === 0) {
        error.value = e instanceof Error ? e.message : 'Fehler beim Laden der Trainer'
      }
    } finally {
      isLoading.value = false
    }
  }

  function $reset(): void {
    instructors.value = []
    isLoading.value = false
    error.value = null
  }

  return {
    instructors,
    isLoading,
    error,
    fetchAll,
    $reset,
  }
})
